import React from "react";
import { Shield, Lock, Eye, Key, Scale, CheckCircle2 } from "lucide-react";

const SecurityAndPrivacy = () => {
  const varaenPrinciples = [
    { letter: "V", title: "Veiled", desc: "Your data is shielded through zero-knowledge privacy architectures." },
    { letter: "A", title: "Autonomous", desc: "Real-time threat detection and response operating continuously in the background." },
    { letter: "R", title: "Restricted", desc: "Zero-trust boundaries ensuring your information is never accessed without permission." },
    { letter: "A", title: "Assured", desc: "Deterministic integrity and confidentiality across every ecosystem service." },
    { letter: "E", title: "Encrypted", desc: "Complete end-to-end cryptographic protection for all data in motion and at rest." },
    { letter: "N", title: "Nexus", desc: "A unified control plane harmonizing privacy safeguards across every device and model." },
  ];

  return (
    <div className="min-h-screen bg-black text-white font-secondary selection:bg-[#065DC0] selection:text-white">
      {/* Subtle ambient lighting */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-16 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(circle,rgba(6,93,192,0.14)_0%,transparent_70%)] blur-3xl" />
        <div className="absolute top-[45%] right-[-5%] w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(59,130,246,0.08)_0%,transparent_70%)] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-20 md:py-28">
        {/* Apple-style Hero Section */}
        <div className="text-center mb-20">
          {/* Official VARAEN Metallic V Logo */}
          <div className="relative mx-auto mb-8 w-24 h-24 sm:w-28 sm:h-28">
            <div className="absolute inset-[-18px] rounded-full bg-[radial-gradient(circle,rgba(6,93,192,0.45)_0%,transparent_70%)]" />
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-3xl overflow-hidden border border-[#065DC0]/40 shadow-2xl shadow-[#065DC0]/30 bg-[#07090e]">
              <img src="/assets/varaen-logo.jpg" alt="FOZZIL VARAEN" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Slogan */}
          <h1 className="font-primary text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.08]">
            Your Intelligence.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-300">
              Unseen. Untouched.
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed font-normal">
            Privacy is a fundamental right. At FOZZIL, it is the foundation of everything we build. Great intelligence should never come at the expense of your privacy.
          </p>

          <p className="text-xs text-gray-500 font-mono mt-4">
            Updated October 2026
          </p>
        </div>

        {/* Foundation Callout */}
        <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.08] mb-16 text-center sm:text-left">
          <p className="text-xl sm:text-2xl font-light text-gray-200 leading-relaxed mb-4">
            FOZZIL was founded on a simple premise: intelligence without compromise.
          </p>
          <p className="text-base text-gray-400 leading-relaxed">
            Security isn’t an afterthought or an added feature — it is embedded into the core of our platform. We design systems so your personal data stays yours, your controls remain absolute, and our autonomous safeguards run silently and continuously.
          </p>
        </div>

        {/* VARAEN: Central Security & Privacy Platform */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="p-2 rounded-xl bg-[#065DC0]/15 text-blue-400 border border-[#065DC0]/30">
              <Shield className="w-5 h-5" />
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              FOZZIL VARAEN
            </h2>
          </div>

          <p className="text-gray-300 leading-relaxed mb-6 text-base sm:text-lg">
            All FOZZIL websites, applications, and intelligent services are unified under <strong>VARAEN</strong> — our zero-trust privacy and security control plane. VARAEN enforces continuous protection, device identity verification, and privacy boundaries across our entire ecosystem.
          </p>

          {/* Clean VARAEN Pillars Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-8">
            {varaenPrinciples.map((p) => (
              <div key={p.letter} className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-[#065DC0]/40 transition-colors">
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span className="w-7 h-7 rounded-lg bg-[#065DC0]/20 text-blue-400 font-mono font-bold text-xs flex items-center justify-center">
                    {p.letter}
                  </span>
                  <h3 className="text-base font-semibold text-white">{p.title}</h3>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          {/* S.H.A.D.E. Defense Engine Card */}
          <div className="p-6 sm:p-7 rounded-2xl bg-[#070b14] border border-[#065DC0]/25 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-2xl bg-[#065DC0]/10 border border-[#065DC0]/30 flex items-center justify-center p-2.5">
              <img src="/S.H.A.D.E logo-wobg.png" alt="S.H.A.D.E. Defense Engine" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="text-[11px] font-mono uppercase tracking-widest text-blue-400 mb-1">Autonomous Defense Engine</div>
              <h3 className="text-lg font-bold text-white mb-2">S.H.A.D.E.</h3>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                Operating directly inside VARAEN, <strong>S.H.A.D.E.</strong> (Secure Hostile Adaptive Defence Engine) is our dedicated defense mechanism. It monitors system telemetry in real time, isolates hostile anomalies, and contains threats automatically to preserve ecosystem integrity.
              </p>
            </div>
          </div>
        </section>

        {/* End-to-End Protection */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="p-2 rounded-xl bg-white/[0.05] text-gray-300 border border-white/10">
              <Lock className="w-5 h-5" />
            </span>
            <h2 className="text-2xl font-bold text-white tracking-tight">
              End-to-End Encryption
            </h2>
          </div>
          <p className="text-gray-300 leading-relaxed text-base sm:text-lg">
            All data is encrypted in transit and at rest with state-of-the-art cryptographic safeguards. Wherever possible, we employ client-side encryption so that even FOZZIL cannot access your plaintext data. Your sensitive records remain protected from end to end.
          </p>
        </section>

        {/* Privacy by Design & Default */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="p-2 rounded-xl bg-white/[0.05] text-gray-300 border border-white/10">
              <Eye className="w-5 h-5" />
            </span>
            <h2 className="text-2xl font-bold text-white tracking-tight">
              Privacy by Design & Default
            </h2>
          </div>
          <p className="text-gray-400 mb-4 text-base">
            We build privacy into every product architecture, adhering to clear, transparent principles:
          </p>
          <ul className="space-y-3 text-gray-300 text-sm sm:text-base">
            <li className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <span><strong>No data mining:</strong> We never sell, rent, or monetize your personal data with third parties.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <span><strong>No public model training:</strong> Your private conversations, enterprise data, and personal records are never used to train public foundation models without your explicit consent.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <span><strong>Irreversible data deletion:</strong> When you delete data, it is permanently purged across our systems.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <span><strong>Data minimization:</strong> We collect only the minimum information required to deliver the service you requested.</span>
            </li>
          </ul>
        </section>

        {/* Secure Access & Identity */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="p-2 rounded-xl bg-white/[0.05] text-gray-300 border border-white/10">
              <Key className="w-5 h-5" />
            </span>
            <h2 className="text-2xl font-bold text-white tracking-tight">
              Secure Access & Identity
            </h2>
          </div>
          <p className="text-gray-300 leading-relaxed mb-4 text-base sm:text-lg">
            Strong authentication and intelligent session controls safeguard your account:
          </p>
          <ul className="space-y-2.5 text-gray-400 text-sm sm:text-base">
            <li>• Password-free passkeys and biometric authentication for phishing-resistant access.</li>
            <li>• Multi-factor verification for privileged and administrative actions.</li>
            <li>• Granular role-based access boundaries across teams and organizations.</li>
            <li>• Automatic session timeout after periods of inactivity.</li>
          </ul>
        </section>

        {/* Ethical Intelligence */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="p-2 rounded-xl bg-white/[0.05] text-gray-300 border border-white/10">
              <Scale className="w-5 h-5" />
            </span>
            <h2 className="text-2xl font-bold text-white tracking-tight">
              Ethical Intelligence & T.A.E.L.S.
            </h2>
          </div>
          <p className="text-gray-300 leading-relaxed text-base sm:text-lg mb-3">
            Our foundational intelligence architecture, <strong>T.A.E.L.S.</strong> (Truly Adaptive, Efficient & Learning Synthesis), is engineered with ethics and privacy at its root. Real-time bias mitigation, human agency for sensitive decisions, and strict validation guardrails ensure AI systems respect your intent, boundaries, and privacy at every turn.
          </p>
          <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
            T.A.E.L.S. operates under zero-knowledge isolation: private conversations, voice telemetry, and file attachments are strictly protected and never used to train public models without your explicit consent.
          </p>
        </section>

        {/* Apple-style Slogan Blockquote */}
        <div className="my-16 p-8 sm:p-12 rounded-3xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.08] text-center">
          <p className="text-2xl sm:text-3xl font-medium tracking-tight text-white mb-3">
            Your Intelligence. Unseen. Untouched.
          </p>
          <p className="text-xs font-mono text-[#38bdf8] uppercase tracking-widest">
            FOZZIL VARAEN · Continuous Zero-Trust Protection
          </p>
        </div>

        {/* Security Contact */}
        <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/[0.08] text-center">
          <h3 className="text-xl font-bold text-white mb-2">Security & Vulnerability Reporting</h3>
          <p className="text-sm text-gray-400 max-w-lg mx-auto mb-6">
            If you have identified a potential security issue or have questions about how FOZZIL protects your data, please contact our security team:
          </p>
          <a
            href="mailto:security@fozzil.in"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#065DC0] hover:bg-[#0077ed] text-white font-semibold text-sm transition-colors shadow-lg shadow-[#065DC0]/25"
          >
            <span>security@fozzil.in</span>
          </a>
          <p className="text-xs text-gray-500 mt-4">
            We aim to review and respond to security inquiries within 2 business hours.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecurityAndPrivacy;
